import React, { useState } from 'react'
import { Alert, Button, Upload } from 'element-react'
import Papa from 'papaparse'
import ErrorAlert from '../../../components/views/includes/ErrorAlert'
import Spacer from '../../../components/views/includes/Spacer'
import CustomLoader from '../../../components/user/includes/CustomLoader'
import { padIdWithZeroes } from '../../../helpers/util'
import useAdmin from '../../../hooks/useAdmin'
import useAuth from '../../../hooks/useAuth'

const jsonFromFile = rawFile => {
    return new Promise((resolve, reject) =>
        Papa.parse(rawFile, {
            complete: results => resolve(results.data),
            error: error => reject(error),
            dynamicTyping: false,
            header: true,
            skipEmptyLines: true,
        }),
    )
}

const Csv = {
    SOLD_TO_ID: 'Sold To ID',
    EMAIL: 'Email',
    FIRST_NAME: 'First Name',
    LAST_NAME: 'Last Name',
}
const usersFromJson = json => {
    return json
        .map((row, index) => ({
            companyId: padIdWithZeroes(row[Csv.SOLD_TO_ID]),
            email: row[Csv.EMAIL],
            firstName: row[Csv.FIRST_NAME],
            lastName: row[Csv.LAST_NAME],
            csvRowNumber: index + 2, // first index (0) is 2nd row from CSV (1st is header)
        }))
        .filter(row => row?.companyId !== '0000000000')
}

const uniqueCompanyIdsFromUsers = users => {
    const uniqueCompanyIds = new Set()
    users.forEach(user => uniqueCompanyIds.add(user.companyId))
    return Array.from(uniqueCompanyIds)
}

const AddMultipleUsers = () => {
    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [wasSuccessful, setWasSuccessful] = useState(false)
    const [usersWithErrors, setUsersWithErrors] = useState([])
    const { checkForValidCompanyIds } = useAdmin()
    const { addUsersAndReturnErrors } = useAuth()

    const handleFile = async rawFile => {
        setIsLoading(true)

        // Extract users and contain errors
        let json = []
        try {
            json = await jsonFromFile(rawFile)
        } catch (e) {
            setError('An error ocurred while parsing the CSV file.')
            console.error(e)
            // CSV processing didn't necessarily fail so continue...
        }
        const usersToAdd = usersFromJson(json)

        // Verify company ID's and set aside users with bad ID's
        const companyIdsToVerify = uniqueCompanyIdsFromUsers(usersToAdd)
        let validCompanyIds = []
        if (companyIdsToVerify.length > 0) {
            try {
                validCompanyIds = await checkForValidCompanyIds(
                    companyIdsToVerify,
                )
            } catch (e) {
                console.error('Verifying company IDs error: ', e)
                // Continue with no valid IDs so we display all the failed users
            }
        } else {
            // No companies means no users so we're done.
            setError('The CSV contained no user data.')
            setIsLoading(false)
        }
        const usersWithInvalidCompanyIds = usersToAdd
            .filter(user => !validCompanyIds.includes(user.companyId))
            .map(user => ({
                ...user,
                error:
                    user.companyId === '' || user.companyId === undefined
                        ? 'No company ID'
                        : 'Invalid company ID',
            }))
        setUsersWithErrors(prevUsers => [
            ...prevUsers,
            ...usersWithInvalidCompanyIds,
        ])

        // Add users with valid company ID's and record any problems
        const usersWithValidCompanyIds = usersToAdd.filter(user =>
            validCompanyIds.includes(user.companyId),
        )

        if (usersWithValidCompanyIds.length > 0) {
            try {
                const usersNotAdded = await addUsersAndReturnErrors(
                    usersWithValidCompanyIds,
                )
                setUsersWithErrors(prevUsers => [
                    ...prevUsers,
                    ...usersNotAdded,
                ])
            } catch (e) {
                setError('An error ocurred while adding new users.')
                console.error('Adding users error: ', e)
            }
            setWasSuccessful(true)
        }
        setIsLoading(false)
    }

    return (
        <React.Fragment>
            {isLoading && <CustomLoader text="Processing user data" />}

            <h4>Add Multiple Users</h4>
            <p>Upload a CSV file to add multiple users at once</p>
            {error && <ErrorAlert error={error} />}

            {/* This is kind of a hack - using the Upload component to get
                the file for local processing - but it's an easy way to match
                the UI as required. If the Input component could be used with a
                type="file" prop and still look the same, that would be ideal. */}
            <Upload
                action=""
                beforeUpload={rawFile => {
                    // Reset old status/errors first
                    setError('')
                    setIsLoading(false)
                    setUsersWithErrors([])
                    setWasSuccessful(false)

                    handleFile(rawFile)

                    // Stop the upload process because we just want the file.
                    return false
                }}
                showFileList={false}
            >
                <Button type="default">Upload CSV</Button>
            </Upload>

            <Spacer size="small" />

            {usersWithErrors.length > 0 ? (
                <React.Fragment>
                    <div
                        className="columns"
                        style={{ borderLeft: '4px solid transparent' }}
                    >
                        <div className="row gray-color text-small margin-bottom-tiny uppercase">
                            <div className="columns small-1 uppercase">Row</div>
                            <div className="columns small-5 medium-11">
                                <div className="hide-for-medium columns small-11">
                                    Error / Email
                                </div>
                                <div className="show-for-medium">
                                    <div className="columns medium-6">
                                        Error
                                    </div>
                                    <div className="columns medium-6">
                                        Email
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        className="columns rounded"
                        style={{
                            borderLeft: '4px solid #F15025',
                            boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
                        }}
                    >
                        {usersWithErrors
                            .sort((a, b) => a.csvRowNumber - b.csvRowNumber)
                            .map((user, index) => (
                                <div
                                    key={`user-on-row-${user.csvRowNumber}`}
                                    className="row padding-v-tiny"
                                    style={{
                                        backgroundColor:
                                            index % 2 === 0
                                                ? '#eeeeee'
                                                : undefined,
                                    }}
                                >
                                    <div className="columns small-1">
                                        {user.csvRowNumber}
                                    </div>
                                    <div className="columns small-11">
                                        <div className="">
                                            <div
                                                className="columns medium-6 bold"
                                                style={{
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {user.error}
                                            </div>
                                            <div
                                                className="hide-for-medium columns medium-6 small-gray-color gray-color"
                                                style={{
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {user.email}
                                            </div>
                                            <div
                                                className="show-for-medium columns medium-6 medium-dark-gray-color"
                                                style={{
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                            >
                                                {user.email}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                    </div>
                </React.Fragment>
            ) : null}
            {wasSuccessful && usersWithErrors.length === 0 ? (
                <Alert
                    showIcon={true}
                    className="margin-bottom-small"
                    description="All users in the CSV have been added."
                    title="Success"
                    type="success"
                    closable={true}
                />
            ) : null}
        </React.Fragment>
    )
}

export default AddMultipleUsers
